'use client';
import React, { useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from '@benzinga/core-ui';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa6';

import { PostElapsed } from '@benzinga/news';
import { formatImageUrl } from '@benzinga/utils';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { QuotesManager } from '@benzinga/quotes-manager';
import { SessionContext } from '@benzinga/session-context';

import styles from './styles.module.scss';

export { MenuList } from '../../../src/components/Edge/StockRankings/MenuList';

export const QuoteCard = ({ hideTicker = false, href = '', showLogo = false, ticker, variant = '' }) => {
  'use memo';
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const session = useContext(SessionContext);

  useEffect(() => {
    if (!ticker?.symbol) return;
    const fetchQuoteLogo = async () => {
      const quoteManager = session.getManager(QuotesManager);
      const logo = await quoteManager.getQuotesLogos([ticker?.symbol]);
      if (logo.ok) {
        const url = logo.ok[0]?.files?.['mark_light'] ?? logo.ok[0]?.files?.['logo_light'] ?? null;
        setLogoUrl(url);
      }
    };
    if (showLogo) {
      fetchQuoteLogo();
    }
  }, [session, showLogo, ticker?.symbol]);

  return (
    <>
    <ErrorBoundary name="QuoteCard">
      <a
        className={
          styles['quote-card'] +
          (variant === 'list' ? ' ' + styles['list-item'] : '') +
          (variant === 'grid' ? ' ' + styles['grid-item'] : '')
        }
        href={href || (hideTicker ? `/markets` : `/quote/${ticker?.symbol}`)}
        rel="noopener noreferrer"
        target="_blank"
      >
        <div className={styles['quote-card-header']}>
          <div className="flex flex-row items-center">
            {showLogo && (
              <div className={styles['quote-card-logo']}>
                {logoUrl && (
                  <img alt={ticker?.symbol} src={logoUrl} />
                )}
              </div>
            )}
            <div className={styles['quote-card-name'] + `${hideTicker ? ' blur-md' : ''}`}>
              <h4>{ticker?.symbol}</h4>
              {(ticker?.name || ticker?.companyName) && <div>{ticker?.name ?? ticker?.companyName}</div>}
              {ticker?.type === 'CRYPTO' && ticker?.description && (
                <div>{ticker.description.replace(/ - United States dollar$/i, '')}</div>
              )}
            </div>
          </div>
        </div>
        <div className={styles['quote-card-price']}>
          <div>${ticker?.lastTradePrice ?? ticker?.price}</div>
          <span className={ticker?.changePercent > 0 ? 'text-green-500' : 'text-red-500'}>
            {ticker?.changePercent > 0 ? <FaCaretUp size={12} /> : <FaCaretDown size={12} />}
            {ticker?.changePercent}%
          </span>
        </div>
      </a>
    </ErrorBoundary>
  </>
  );
};  

export const ArticleListItem = ({ article, showTeaser = false }) => {
  const imageSrc = article?.image && formatImageUrl({ image: article.image }, true, false);

  if (!article) {
    return null;
  }
  return (
    <a
      className={styles['article-list-item'] + ' flex flex-row gap-2'}
      href={`/article/${article?.id}`}
      key={article?.id}
      rel="noopener noreferrer"
      target="_blank"
    >
      {article.image && (
        <div className={'overflow-hidden ' + showTeaser ? styles['full-item'] : styles['short-item']}>
          <div className={styles['article-thumb']}>
            <img alt="" className="post-thumb w-full h-full object-cover" src={imageSrc} />
          </div>
        </div>
      )}
      <div className={styles['article-post-card']}>
        <div className="flex flex-col">
          <h4
            className="w-full overflow-ellipsis line-clamp-2"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.title) }}
          ></h4>
          {showTeaser && (
            <div
              className="text-sm text-[#192940] w-full items-center overflow-hidden overflow-ellipsis line-clamp-2"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.teaser) }}
            ></div>
          )}
        </div>
        <div className="flex flex-row justify-between items-center mt-1">
          <div className="flex flex-row gap-1 text-xs">
            {article?.tickers?.slice(0, 2)?.map(ticker => {
              return (
                <div className="p-1 bg-bzblue-600/10 rounded-sm" key={ticker.name}>
                  {ticker?.name}
                </div>
              );
            })}
            {article?.tickers?.length > 3 && (
              <div className="p-1 bg-bzblue-600/10 rounded-sm">+ {article.tickers.length - 2} more</div>
            )}
          </div>
          <PostElapsed
            className="font-semibold text-gray-500 post-created-at mr-1"
            created={article?.created}
            dateType={article?.dateType}
          />
        </div>
      </div>
    </a>
  );
};
